/**
 * AI Chat Usage Tracking Service
 * Tracks AI chat interactions for admin analytics and monitoring
 */

import firebase from "firebase/compat/app";
import "firebase/compat/firestore";
import { v4 as uuidv4 } from "uuid";

const db = firebase.firestore();

const TOOL_CALL_REGEX_SOURCE = "\\[\\[tool-call:([\\w-]+)(?:\\|([^\\]]+))?\\]\\]";
const createToolCallRegex = () => new RegExp(TOOL_CALL_REGEX_SOURCE, "g");

/**
 * Extracts tool calls from AI response text
 * @param {string} text - Response text to extract tool calls from
 * @returns {Array<{id: string, payload?: object}>} Array of tool calls
 */
function extractToolCalls(text) {
	const regex = createToolCallRegex();
	const matches = [];
	let match;

	while ((match = regex.exec(text || "")) !== null) {
		let payload = null;

		if (match[2]) {
			try {
				payload = JSON.parse(match[2]);
			} catch (parseError) {
				console.warn(
					"[aiChatUsageTracking][extractToolCalls] - Failed to parse tool payload:",
					match[2],
					parseError
				);
				payload = match[2];
			}
		}

		matches.push({
			id: match[1],
			payload,
		});
	}

	return matches;
}

/**
 * Tracks AI chat usage for admin analytics
 * @param {Object} params - Usage tracking parameters
 * @param {string} params.userId - User ID
 * @param {string} params.message - User's message
 * @param {string} params.response - AI response text
 * @param {number} params.responseTime - Response time in milliseconds
 * @param {Array} params.conversationHistory - Conversation history array
 * @param {boolean} params.success - Whether the request was successful
 * @param {string} params.errorType - Error type if failed (optional)
 * @param {string} params.errorMessage - Error message if failed (optional)
 * @param {boolean} params.isStreamed - Whether response was streamed
 * @returns {Promise<void>}
 */
export async function trackAIChatUsage({
	userId,
	message,
	response,
	responseTime,
	conversationHistory = [],
	success = true,
	errorType = null,
	errorMessage = null,
	isStreamed = false,
}) {
	if (!userId) {
		console.warn("[aiChatUsageTracking][trackAIChatUsage] - No userId provided, skipping tracking");
		return;
	}

	try {
		const timestamp = firebase.firestore.FieldValue.serverTimestamp();
		const messageId = `${Date.now()}_${uuidv4()}`;

		// Extract tool calls from response
		const toolCalls = extractToolCalls(response || "");

		// Build usage data with all required fields always present
		// Required fields must never be null/undefined
		const usageData = {
			// Required fields - always present
			timestamp,
			userId,
			messageLength: typeof message === "string" ? message.length : 0,
			responseLength: typeof response === "string" ? response.length : 0,
			responseTime: typeof responseTime === "number" && !isNaN(responseTime) ? responseTime : 0,
			model: "gemini-2.5-flash",
			toolCalls: Array.isArray(toolCalls) ? toolCalls.map(call => {
				// Only include payload if it's not null
				const toolCall = { id: call.id || "" };
				if (call.payload !== null && call.payload !== undefined) {
					toolCall.payload = call.payload;
				}
				return toolCall;
			}) : [],
			success: typeof success === "boolean" ? success : true,
			isStreamed: typeof isStreamed === "boolean" ? isStreamed : false,
			conversationHistoryLength: Array.isArray(conversationHistory) ? conversationHistory.length : 0,
		};

		// Optional fields - only include if they have values
		if (errorType !== null && errorType !== undefined && errorType !== "") {
			usageData.errorType = errorType;
		}
		if (errorMessage !== null && errorMessage !== undefined && errorMessage !== "") {
			usageData.errorMessage = errorMessage;
		}

		// Validate that document has required fields before writing
		const requiredFields = ["timestamp", "userId", "messageLength", "responseLength", 
		                       "responseTime", "model", "toolCalls", "success", 
		                       "isStreamed", "conversationHistoryLength"];
		const hasAllRequiredFields = requiredFields.every(field => {
			const value = usageData[field];
			// Check if field exists and is not null/undefined
			if (value === null || value === undefined) {
				return false;
			}
			// Arrays are valid even if empty
			if (Array.isArray(value)) {
				return true;
			}
			// For other types, ensure they have a valid value
			return true;
		});

		if (!hasAllRequiredFields) {
			console.warn(
				"[aiChatUsageTracking][trackAIChatUsage] - Document missing required fields, skipping write",
				usageData
			);
			return;
		}

		// Ensure document has at least one field (safety check)
		if (Object.keys(usageData).length === 0) {
			console.warn(
				"[aiChatUsageTracking][trackAIChatUsage] - Document would be empty, skipping write"
			);
			return;
		}

		// Write to Firestore (non-blocking)
		// Structure: /Admin/AiChatUsage/{userId}/messages/{messageId}
		// Admin collection -> AiChatUsage document -> userId subcollection -> userId document -> messages subcollection -> messageId document
		db.collection("Admin")
			.doc("AiChatUsage")
			.collection(userId)
			.doc(userId)
			.collection("messages")
			.doc(messageId)
			.set(usageData)
			.catch(error => {
				console.error(
					"[aiChatUsageTracking][trackAIChatUsage] - Error writing usage data:",
					error
				);
			});
	} catch (error) {
		console.error(
			"[aiChatUsageTracking][trackAIChatUsage] - Error tracking usage:",
			error
		);
		// Don't throw - tracking failures shouldn't break chat functionality
	}
}

